import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react';
import { clamp, isNil } from 'lodash';
import { Icon, KeyCodes } from 'venn-ui-kit';
import { Numbers } from 'venn-utils';
import { cn } from '@/lib/utils';

const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 12;
const ACTIONS_WIDTH = 20;

const toFixedRound = (value: number, precision: number) => {
  const f = 10 ** precision;
  return (Math.round(Numbers.correctFloat(value * f)) / f).toFixed(precision);
};

const isOutOfRange = (v: number, min: number | undefined, max: number | undefined) =>
  v < (min ?? -Infinity) || v > (max ?? Infinity);

type Align = 'left' | 'center' | 'right';

interface BaseNumberInputProps {
  name?: string;
  className?: string;
  /** Class applied directly to the inner `<input>` element. */
  inputClassName?: string;
  originalValue?: number;
  precision?: number;
  step?: number;
  min?: number;
  max?: number;
  error?: boolean;
  formatter?: (value: string) => string;
  parser?: (value: string) => string;
  /**
   * Appends a unit symbol (e.g. `"%"`) to the displayed value, and strips it on parse.
   * Generates formatter + parser automatically; overridden by explicit `formatter`/`parser`.
   */
  unitSymbol?: string;
  width?: number;
  height?: number;
  /** Shrink font to fit the numeric `width` when the text overflows. Requires a numeric `width`. */
  autoFitFont?: boolean;
  /** Text alignment of the value inside the input. */
  align?: Align;
  /** Hide the +/- stepper buttons. */
  hideSteppers?: boolean;
  /** Label to be used for the aria-label of the input */
  label?: string;
  controlled?: boolean;
  /**
   * Formats the number for *display only*, independent of `precision` (which rounds the committed
   * value). Use to show padded/extended decimals without altering the stored value. Receives the
   * non-null value; the `formatter`/`unitSymbol` decoration is still applied to the result.
   */
  displayFormatter?: (value: number) => string;
  /** Bump to force-sync internalValue from value prop even when value prop didn't change */
  resetTick?: number;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  /** When the committed value is null, +/- steps from this base instead of 0. */
  nullStepBase?: number;
  /**
   * When `allowNull` and a typed value falls outside [min, max], revert to null instead of
   * clamping to the boundary — clamping a negative entry to 0 would silently produce a real
   * committed value the user never asked for.
   */
  rejectOutOfRange?: boolean;
}

interface NumberInputAllowNullProps extends BaseNumberInputProps {
  allowNull: true;
  value?: number | null;
  onChange?: (value: number | null) => void;
}

interface NumberInputDefaultProps extends BaseNumberInputProps {
  allowNull?: false;
  value?: number;
  onChange?: (value: number) => void;
}

export type NumberInputProps = NumberInputAllowNullProps | NumberInputDefaultProps;

const toDisplayText = (
  value: number | null | undefined,
  formatter: ((s: string) => string) | undefined,
  precision: number | undefined,
  displayFormatter: ((value: number) => string) | undefined,
) => {
  if (isNil(value)) {
    return '';
  }

  // displayFormatter governs only the rendered text, never the committed value — so a field can show
  // padded/extended precision (e.g. "5.00") while still storing the user's exact number.
  const str = displayFormatter
    ? displayFormatter(value)
    : isNil(precision)
      ? String(value)
      : toFixedRound(value, precision);

  return formatter ? formatter(str) : str;
};

const ALIGN_CLASS: Record<Align, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const STEPPER_BTN =
  'flex items-center justify-center h-1/2 p-0 border-0 bg-transparent text-venn-black transition-colors cursor-pointer hover:text-venn-black disabled:cursor-not-allowed disabled:text-venn-grey-60 disabled:hover:text-venn-grey-60';

const NumberInput = ({
  onChange,
  value,
  step = 1,
  controlled,
  resetTick,
  max,
  min,
  precision,
  width,
  height = 26,
  align = 'center',
  autoFitFont,
  hideSteppers,
  label,
  originalValue,
  onFocus,
  onBlur,
  allowNull,
  placeholder,
  nullStepBase,
  rejectOutOfRange,
  displayFormatter,
  formatter,
  parser,
  unitSymbol,
  inputClassName,
  error,
  disabled,
  className,
  ...rest
}: NumberInputProps) => {
  const onChangeInternal = onChange as ((value: number | null) => void) | undefined;

  const resolvedFormatter = useMemo(
    () => formatter ?? (unitSymbol ? (v: string) => `${v}${unitSymbol}` : undefined),
    [formatter, unitSymbol],
  );

  const resolvedParser = useMemo(
    () => parser ?? (unitSymbol ? (v: string) => v.replaceAll(unitSymbol, '') : undefined),
    [parser, unitSymbol],
  );

  const [text, setText] = useState<string>(() => {
    return toDisplayText(allowNull ? (value ?? null) : (value ?? 0), resolvedFormatter, precision, displayFormatter);
  });
  const [focused, setFocused] = useState(false);
  const [fontSize, setFontSize] = useState(MAX_FONT_SIZE);

  const committedRef = useRef<number | null>(allowNull ? (value ?? null) : (value ?? 0));
  const composingRef = useRef(false);
  const focusedRef = useRef(false);
  const sizerRef = useRef<HTMLSpanElement | null>(null);
  const textRef = useRef(text);
  textRef.current = text;

  const syncFromProp = useCallback(() => {
    const numeric = allowNull ? (value ?? null) : (value ?? 0);
    committedRef.current = numeric;
    setText(toDisplayText(numeric, resolvedFormatter, precision, displayFormatter));
  }, [allowNull, value, resolvedFormatter, precision, displayFormatter]);

  // E1: resetTick — always resync, even focused (ScatterAxis bounce-back on rejected edit,
  // where value prop is unchanged but resetTick bumps to force the field back).
  useEffect(() => {
    if (resetTick === undefined) {
      return;
    }

    syncFromProp();
    // intentionally omit syncFromProp: runs only on resetTick change, reads latest closure values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTick]);

  // E2: controlled value prop — resync only when not focused (don't clobber in-progress typing).
  // A disabled field can't have in-progress typing to protect, so always reflect the prop.
  useEffect(() => {
    if (!controlled) {
      return;
    }

    if (focusedRef.current && !disabled) {
      return;
    }

    syncFromProp();
  }, [controlled, syncFromProp, disabled]);

  // E3: a disabled field must not retain focus state (e.g. removing the benchmark while its
  // tracking error input is focused) — drop the focus ring so it reads as disabled.
  useEffect(() => {
    if (disabled && focusedRef.current) {
      focusedRef.current = false;
      setFocused(false);
    }
  }, [disabled]);

  useLayoutEffect(() => {
    if (!autoFitFont || isNil(width)) {
      return;
    }

    const needed = sizerRef.current?.scrollWidth ?? 0;
    setFontSize(needed > width ? clamp((MAX_FONT_SIZE * width) / needed, MIN_FONT_SIZE, MAX_FONT_SIZE) : MAX_FONT_SIZE);
  }, [text, width, autoFitFont]);

  const constrain = useCallback(
    (v: number): number => {
      const result = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
      if (!isNil(precision)) {
        return parseFloat(toFixedRound(result, precision));
      }

      return result;
    },
    [max, min, precision],
  );

  const commit = useCallback(
    (currentText: string) => {
      let next: number | null;

      if (currentText === '') {
        next = allowNull ? null : committedRef.current;
      } else {
        const committed = committedRef.current;
        const isUnmodified =
          committed !== null &&
          currentText === toDisplayText(committed, resolvedFormatter, precision, displayFormatter);
        let parsed: number | null;
        if (isUnmodified) {
          // committed can pre-date rejectOutOfRange (e.g. synced in from a controlled `value` prop),
          // so an already out-of-range value still needs rejecting even though the text wasn't retyped.
          parsed = rejectOutOfRange && allowNull && isOutOfRange(committed, min, max) ? null : committed;
        } else {
          const cleaned = resolvedParser ? resolvedParser(currentText) : currentText.replace(/[^\w.-]+/g, '');
          const num = Number(cleaned);
          if (Number.isNaN(num)) {
            parsed = allowNull ? null : 0;
          } else if (rejectOutOfRange && isOutOfRange(num, min, max)) {
            // reject the edit outright rather than clamping to the boundary (a real committed value
            // the user never asked for) or resetting to 0 (which may itself be out of range).
            parsed = allowNull ? null : committed;
          } else {
            parsed = num;
          }
        }
        next = parsed === null ? null : constrain(parsed);
      }

      committedRef.current = next;
      setText(toDisplayText(next, resolvedFormatter, precision, displayFormatter));
      onChangeInternal?.(next ?? null);
    },
    [
      allowNull,
      resolvedParser,
      constrain,
      resolvedFormatter,
      precision,
      displayFormatter,
      onChangeInternal,
      min,
      max,
      rejectOutOfRange,
    ],
  );

  const doStep = useCallback(
    (dir: 1 | -1) => {
      const base = committedRef.current ?? nullStepBase ?? 0;
      const dec = precision ?? Numbers.decimalCount(step);
      const raw = parseFloat(toFixedRound(base + dir * step, dec));
      const next =
        rejectOutOfRange && allowNull && isOutOfRange(raw, min, max)
          ? null
          : Math.min(max ?? Infinity, Math.max(min ?? -Infinity, raw));

      committedRef.current = next;
      setText(toDisplayText(next, resolvedFormatter, precision, displayFormatter));
      onChangeInternal?.(next);
    },
    [
      step,
      precision,
      max,
      min,
      nullStepBase,
      resolvedFormatter,
      displayFormatter,
      onChangeInternal,
      rejectOutOfRange,
      allowNull,
    ],
  );

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!composingRef.current) {
      setText(e.target.value);
    }
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === KeyCodes.Enter && !composingRef.current) {
        commit(textRef.current);
      }
    },
    [commit],
  );

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
  }, []);

  const onCompositionEnd = useCallback(() => {
    composingRef.current = false;
  }, []);

  const onFocusHandler = useCallback(() => {
    focusedRef.current = true;
    setFocused(true);
    onFocus?.();
  }, [onFocus]);

  const onBlurHandler = useCallback(() => {
    focusedRef.current = false;
    setFocused(false);
    onBlur?.();
    commit(textRef.current);
  }, [onBlur, commit]);

  const highlight = !isNil(originalValue) && originalValue !== committedRef.current;

  const valueColorClass = error ? 'text-venn-error' : highlight ? 'text-venn-highlight-dark' : 'text-venn-black';

  const childHoverBorderClass =
    disabled || error ? undefined : 'group-hover:border-venn-dark-blue group-hover:divide-venn-dark-blue';

  return (
    <>
      {autoFitFont && (
        <span
          ref={sizerRef}
          aria-hidden
          className="invisible absolute top-[-9999px] left-[-9999px] whitespace-pre font-normal"
          style={{ fontSize: MAX_FONT_SIZE }}
        >
          {text}
        </span>
      )}
      <div
        className={cn(
          'group relative inline-flex items-center align-middle m-0 p-0 text-[12px] bg-white rounded border border-solid transition-colors',
          error ? 'border-venn-error' : 'border-venn-grey-40',
          focused && (error ? 'border-venn-error' : 'border-venn-dark-blue ring-2 ring-venn-dark-blue/20'),
          !focused && !disabled && !error && 'hover:border-venn-dark-blue',
          className,
        )}
        style={{ height: `${height}px`, lineHeight: `${height}px` }}
      >
        <input
          className={cn(
            'h-full font-normal bg-transparent rounded border-0 outline-none transition-colors focus:outline-none',
            autoFitFont ? 'px-0' : 'pl-2 pr-2',
            isNil(width) && 'w-full',
            ALIGN_CLASS[align],
            valueColorClass,
            'placeholder:text-venn-grey-60 disabled:placeholder:text-venn-grey-40',
            'disabled:bg-venn-grey-10 disabled:cursor-not-allowed disabled:text-venn-grey-60',
            inputClassName,
          )}
          style={{
            ...(!isNil(width) && { width: `${width}px` }),
            ...(autoFitFont && { fontSize: `${fontSize}px` }),
          }}
          role="spinbutton"
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={committedRef.current ?? undefined}
          value={text}
          disabled={disabled}
          placeholder={allowNull ? placeholder : undefined}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          {...rest}
        />
        {!hideSteppers && (
          <div
            className={cn(
              'flex flex-col h-full text-[8px] divide-y divide-solid border-0 border-l border-solid transition-colors',
              error ? 'border-venn-error divide-venn-error' : 'border-venn-grey-40 divide-venn-grey-40',
              focused &&
                (error ? 'border-venn-error divide-venn-error' : 'border-venn-dark-blue divide-venn-dark-blue'),
              childHoverBorderClass,
            )}
            style={{ width: ACTIONS_WIDTH }}
          >
            <button
              type="button"
              aria-label="Increase Value"
              tabIndex={-1}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => doStep(1)}
              className={STEPPER_BTN}
            >
              <Icon type="plus" />
            </button>
            <button
              type="button"
              aria-label="Decrease Value"
              tabIndex={-1}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => doStep(-1)}
              className={STEPPER_BTN}
            >
              <Icon type="minus" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NumberInput;
